import { jsx as _jsx } from "react/jsx-runtime";
import { useTabsContext } from '#pplx/frontend/aether/components/Tabs/TabsContext.js';
import cn from 'classnames';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
export const TabsIndicator = memo(function TabsIndicator({ listRef, }) {
    const parentContext = useTabsContext();
    const { variant } = parentContext;
    const observedTabRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const [shouldTransition, setShouldTransition] = useState(false);
    // Track indicator position and height (height used for button variant)
    const [indicatorStyle, setIndicatorStyle] = useState({
        leftPx: 0,
        topPx: 0,
        widthPx: 0,
        heightPx: 0,
        opacity: 0,
    });
    // Update indicator position by measuring active tab
    const updateIndicator = useCallback(() => {
        const listElement = listRef.current;
        if (!listElement)
            return;
        // Use aria-selected (not data-state) because Radix Tooltip's asChild
        // overwrites data-state, whereas aria-selected is owned exclusively by
        // Radix Tabs. We use querySelector instead of the ref map because
        // ResponsiveTabItem renders measurement-shadow duplicates of each tab
        // that register over the real tab's ref.
        const activeTab = listElement.querySelector('[aria-selected="true"]');
        if (!activeTab)
            return;
        // The list is the indicator's containing block. Layout coordinates stay
        // aligned while an ancestor (such as an opening modal) animates its scale.
        const leftPx = activeTab.offsetLeft;
        const widthPx = activeTab.offsetWidth;
        const topPx = activeTab.offsetTop;
        const heightPx = activeTab.offsetHeight;
        setIndicatorStyle({
            leftPx,
            topPx,
            widthPx,
            heightPx,
            opacity: 1,
        });
        // Enable transitions after first successful update
        // Use rAF to ensure the position has been painted first
        if (!shouldTransition) {
            window.requestAnimationFrame(() => {
                setShouldTransition(true);
            });
        }
        // If active tab changed, update observation
        if (activeTab !== observedTabRef.current && resizeObserverRef.current) {
            if (observedTabRef.current) {
                resizeObserverRef.current.unobserve(observedTabRef.current);
            }
            resizeObserverRef.current.observe(activeTab);
            observedTabRef.current = activeTab;
        }
        return activeTab;
    }, [listRef, shouldTransition]);
    // Register this indicator's update function with parent context
    useEffect(() => {
        // @eslint-disable-next-line react-hooks/immutability This is intentional
        parentContext.updateIndicatorRef.current = updateIndicator;
    }, [parentContext, updateIndicator]);
    // Setup ResizeObserver to watch active tab and list size changes
    useEffect(() => {
        const listElement = listRef.current;
        resizeObserverRef.current = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                updateIndicator();
            });
        });
        // Initial setup - observe the current active tab
        const activeTab = updateIndicator();
        if (activeTab) {
            resizeObserverRef.current.observe(activeTab);
            observedTabRef.current = activeTab;
        }
        // Also observe the list element itself for layout changes
        // (e.g., font loading, container resize)
        if (listElement) {
            resizeObserverRef.current.observe(listElement);
        }
        return () => {
            resizeObserverRef.current?.disconnect();
            resizeObserverRef.current = null;
            observedTabRef.current = null;
        };
    }, [updateIndicator, listRef]);
    // Button variant: no sliding indicator — selected state is styled directly on each tab
    if (variant === 'button' || variant === 'button-inverse') {
        return null;
    }
    return (_jsx("span", { className: cn('absolute left-0 pointer-events-none', {
            'transition-[transform,width,height,opacity] duration-300 ease-in-out': shouldTransition,
            // Underline variant: thin bar at the bottom
            'bottom-0 h-two bg-inverse': true,
        }), style: {
            transform: `translateX(${indicatorStyle.leftPx}px)`,
            width: `${indicatorStyle.widthPx}px`,
            opacity: indicatorStyle.opacity,
        }, "aria-hidden": "true" }));
});
//# sourceMappingURL=TabsIndicator.js.map